// Mercapoli - Order Tracking Page
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, MapPin, Phone, Clock, CheckCircle, 
  ChefHat, Bike, Package, Home, Loader2, Map, Bell
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { pedidos as pedidosApi, notifications as notificationsApi } from '../../lib/api';
import { toast } from 'sonner';
import OrderTrackingMap from '../../components/OrderTracking';
import ProximityAlert from '../../components/ProximityAlert';
import NotificationBell from '../../components/NotificationBell';

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [showProximityAlert, setShowProximityAlert] = useState(false);
  const [proximityData, setProximityData] = useState(null);
  const [hasShownProximityAlert, setHasShownProximityAlert] = useState(false);

  // Check for proximity notifications
  const checkProximityNotifications = useCallback(async () => {
    try {
      const data = await notificationsApi.getAll(5);
      const proximityNotif = data.notifications?.find(
        n => n.tipo === 'rider_cerca' && n.pedido_id === id && !n.leida
      );
      
      if (proximityNotif && !hasShownProximityAlert) {
        setProximityData({
          riderName: proximityNotif.mensaje.split(' está')[0] || 'Tu repartidor',
          distanceM: 500,
          estimatedMinutes: 2
        });
        setShowProximityAlert(true);
        setHasShownProximityAlert(true);
        
        // Auto-hide after 15 seconds
        setTimeout(() => setShowProximityAlert(false), 15000);
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [id, hasShownProximityAlert]);

  const loadPedido = useCallback(async () => {
    try {
      const data = await pedidosApi.getById(id);
      setPedido(data);
      
      // Check proximity on first load if en_camino
      if (data.estado === 'en_camino' && !hasShownProximityAlert) {
        checkProximityNotifications();
      }
    } catch (error) {
      toast.error('Error al cargar el pedido');
      navigate('/app/pedidos');
    }
    setLoading(false);
  }, [id, navigate, hasShownProximityAlert, checkProximityNotifications]);

  useEffect(() => {
    loadPedido();
    // Poll for updates every 10 seconds
    const interval = setInterval(() => {
      loadPedido();
      checkProximityNotifications();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadPedido, checkProximityNotifications]);

  const orderSteps = [
    { key: 'pendiente', label: 'Pedido recibido', icon: Package, description: 'Tu pedido ha sido recibido' },
    { key: 'aceptado', label: 'Aceptado', icon: CheckCircle, description: 'El restaurante aceptó tu pedido' },
    { key: 'preparando', label: 'Preparando', icon: ChefHat, description: 'Tu comida está siendo preparada' },
    { key: 'listo', label: 'Listo', icon: Package, description: 'Tu pedido está listo para recoger' },
    { key: 'en_camino', label: 'En camino', icon: Bike, description: 'Tu pedido va en camino' },
    { key: 'entregado', label: 'Entregado', icon: Home, description: '¡Disfruta tu comida!' },
  ];

  const getCurrentStepIndex = () => {
    if (!pedido) return 0;
    const index = orderSteps.findIndex(step => step.key === pedido.estado);
    return index >= 0 ? index : 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      aceptado: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      preparando: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      listo: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
      en_camino: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      entregado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      cancelado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[status] || colors.pendiente;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
      </div>
    );
  }

  if (!pedido) {
    return null;
  }

  const currentStep = getCurrentStepIndex();
  const isCompleted = pedido.estado === 'entregado';
  const isCancelled = pedido.estado === 'cancelado';

  return (
    <div className="min-h-screen bg-background pb-6">
      {/* Proximity Alert */}
      <ProximityAlert
        isVisible={showProximityAlert}
        riderName={proximityData?.riderName}
        distanceM={proximityData?.distanceM}
        estimatedMinutes={proximityData?.estimatedMinutes}
        onClose={() => setShowProximityAlert(false)}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app/pedidos')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-heading text-lg font-bold">Pedido #{id.slice(-6)}</h1>
                <p className="text-sm text-muted-foreground">
                  {new Date(pedido.created_at).toLocaleDateString('es-MX', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(pedido.estado)}>
              {pedido.estado.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        </div>
      </header>

      <div className="container-app py-6 space-y-6">
        {/* Estimated time */}
        {!isCompleted && !isCancelled && pedido.tiempo_estimado_min && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-spicy-coral to-[#FF8E53] rounded-2xl p-6 text-white text-center"
          >
            <Clock className="h-8 w-8 mx-auto mb-2" />
            <p className="text-white/80 text-sm mb-1">Tiempo estimado</p>
            <p className="font-heading text-3xl font-bold">
              {pedido.tiempo_estimado_min} min
            </p>
          </motion.div>
        )}

        {/* Mapa de tracking en tiempo real (solo cuando está en camino) */}
        {pedido.estado === 'en_camino' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <OrderTrackingMap pedidoId={id} height="350px" />
          </motion.div>
        )}

        {/* Botón para ver mapa si no está en camino pero tiene ubicación */}
        {pedido.estado !== 'en_camino' && !isCompleted && !isCancelled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center"
          >
            <Button 
              variant="outline" 
              onClick={() => setShowMap(!showMap)}
              className="gap-2"
            >
              <Map className="h-4 w-4" />
              {showMap ? 'Ocultar mapa' : 'Ver ubicación en mapa'}
            </Button>
          </motion.div>
        )}

        {showMap && pedido.estado !== 'en_camino' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
          >
            <OrderTrackingMap pedidoId={id} height="300px" />
          </motion.div>
        )}

        {/* Order progress */}
        {!isCancelled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-xl border border-border p-6"
          >
            <h2 className="font-heading font-bold mb-6">Estado del pedido</h2>
            
            <div className="relative">
              {/* Progress line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-muted" />
              <div 
                className="absolute left-5 top-0 w-0.5 bg-spicy-coral transition-all duration-500"
                style={{ height: `${(currentStep / (orderSteps.length - 1)) * 100}%` }}
              />
              
              {/* Steps */}
              <div className="space-y-6">
                {orderSteps.map((step, index) => {
                  const isActive = index <= currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <div key={step.key} className="flex items-start gap-4 relative">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all
                        ${isActive 
                          ? 'bg-spicy-coral text-white' 
                          : 'bg-muted text-muted-foreground'
                        }
                        ${isCurrent ? 'ring-4 ring-spicy-coral/30' : ''}
                      `}>
                        <step.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 pt-2">
                        <p className={`font-medium ${isActive ? '' : 'text-muted-foreground'}`}>
                          {step.label}
                        </p>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Cancelled message */}
        {isCancelled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-destructive/10 border border-destructive/30 rounded-xl p-6 text-center"
          >
            <p className="text-destructive font-medium">Este pedido ha sido cancelado</p>
          </motion.div>
        )}

        {/* Delivery address */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-xl border border-border p-4"
        >
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-spicy-coral flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Dirección de entrega</p>
              <p className="text-sm text-muted-foreground">{pedido.direccion_entrega}</p>
            </div>
          </div>
        </motion.div>

        {/* Order details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-xl border border-border p-4"
        >
          <h2 className="font-heading font-bold mb-4">Detalle del pedido</h2>
          
          <div className="space-y-3">
            {pedido.items.map((item, index) => (
              <div key={index} className="flex justify-between">
                <div>
                  <span className="font-medium">{item.cantidad}x</span>
                  <span className="ml-2">{item.nombre}</span>
                  {item.notas && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Nota: {item.notas}
                    </p>
                  )}
                </div>
                <span>${item.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${pedido.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Envío</span>
              <span>${pedido.costo_envio.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-heading font-bold text-lg pt-2">
              <span>Total</span>
              <span className="text-spicy-coral">${pedido.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Método de pago: <span className="font-medium text-foreground capitalize">{pedido.metodo_pago}</span>
            </p>
          </div>
        </motion.div>

        {/* Actions */}
        {isCompleted && (
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" asChild>
              <Link to="/app">
                Volver al inicio
              </Link>
            </Button>
            <Button className="flex-1 bg-spicy-coral hover:bg-spicy-coral/90">
              Repetir pedido
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
